import { useEffect, useRef, useState } from 'react';
import { useLayout } from '../shell/layout';
import { SEVERITY_MARK } from './describe';
import { STICKY, notifications, useNotifications, type Notification } from './notificationStore';

const AUTO_DISMISS_MS = 7_000;

const TONE: Record<Notification['severity'], string> = {
  info: 'border-line',
  success: 'border-up',
  warning: 'border-warn',
  danger: 'border-down',
};

function Toast({ item }: { item: Notification }) {
  const sticky = STICKY.includes(item.severity);
  // The countdown stops while the pointer is over the toast or it has the keyboard, so it never vanishes mid-read.
  const [paused, setPaused] = useState(false);
  const remaining = useRef(AUTO_DISMISS_MS);
  useEffect(() => {
    if (sticky || paused) return;
    const started = Date.now();
    const timer = setTimeout(() => notifications.getState().dismiss(item.id), remaining.current);
    return () => {
      clearTimeout(timer);
      remaining.current = Math.max(1_500, remaining.current - (Date.now() - started));
    };
  }, [item.id, sticky, paused]);

  const mark = SEVERITY_MARK[item.severity];
  return (
    <div
      role={item.severity === 'danger' ? 'alert' : 'status'}
      className={`pointer-events-auto flex w-full items-start sm:w-80 gap-2 rounded-md border-l-4 border bg-panel2 p-3 text-sm shadow-xl ${TONE[item.severity]}`}
      data-testid="toast"
      data-severity={item.severity}
      data-paused={paused}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <span aria-hidden="true">{mark.icon}</span>
      <div className="min-w-0 flex-1">
        <p className="font-semibold">
          <span className="sr-only">{mark.label}: </span>
          {item.title}
        </p>
        <p className="break-words text-muted">{item.text}</p>
      </div>
      <button
        type="button"
        aria-label={`Dismiss: ${item.title}`}
        className="rounded px-1 text-muted hover:text-text"
        onClick={() => notifications.getState().dismiss(item.id)}
      >
        ×
      </button>
    </div>
  );
}

/** Pop-up notifications in the corner: they leave by themselves (not while being read), except alerts, which stay until dismissed. */
export function Toasts() {
  const toasts = useNotifications((s) => s.toasts);
  const { mobile } = useLayout();
  return (
    <div
      // on a phone: full width, above the tab bar
      className={`pointer-events-none fixed z-[900] flex flex-col gap-2 ${
        mobile ? 'inset-x-2 bottom-[calc(4.5rem+env(safe-area-inset-bottom))]' : 'top-16 right-4'
      }`}
      role="log"
      aria-label="Notifications"
    >
      {toasts.map((t) => (
        <Toast key={t.id} item={t} />
      ))}
    </div>
  );
}
